import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { CheckIcon, ChevronDownIcon, XIcon } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@repo/ui-primitives/avatar';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandList } from '@repo/ui-primitives/command';
import { getInitials } from '../../lib/avatar-utils';
import { cn } from '../../lib/utils';
const MAX_DISPLAY_CHARS = 20;
function generateNameString(users) {
    var _users_, _users_1;
    if (users.length === 0) {
        return '';
    }
    if (users.length === 1) {
        var _ref;
        var _users_2;
        const res = (_ref = (_users_2 = users[0]) === null || _users_2 === void 0 ? void 0 : _users_2.name) !== null && _ref !== void 0 ? _ref : '';
        return res.length > MAX_DISPLAY_CHARS ? "".concat(res.slice(0, MAX_DISPLAY_CHARS), "...") : res;
    }
    if (users.length === 2) {
        var _users_3, _users_4;
        return "".concat((_users_3 = users[0]) === null || _users_3 === void 0 ? void 0 : _users_3.name, ", ").concat((_users_4 = users[1]) === null || _users_4 === void 0 ? void 0 : _users_4.name).slice(0, MAX_DISPLAY_CHARS) + '...';
    }
    const base = "".concat((_users_ = users[0]) === null || _users_ === void 0 ? void 0 : _users_.name, ", ").concat((_users_1 = users[1]) === null || _users_1 === void 0 ? void 0 : _users_1.name);
    return base.length > MAX_DISPLAY_CHARS ? "".concat(base.slice(0, MAX_DISPLAY_CHARS), "... +").concat(String(users.length - 2)) : "".concat(base, ", +").concat(String(users.length - 2));
}
function SelectedAvatars(param) {
    let { value } = param;
    if (!value.length) {
        return null;
    }
    const avatarList = value.slice(0, 3);
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex items-center gap-2 min-w-0",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "flex -space-x-2 shrink-0",
                children: avatarList.map((option, index)=>{
                    var _option_avatarColor;
                    return /*#__PURE__*/ _jsxs(Avatar, {
                        className: "size-6 border-2 border-background text-xs",
                        style: {
                            zIndex: avatarList.length - index
                        },
                        children: [
                            option.avatar && /*#__PURE__*/ _jsx(AvatarImage, {
                                src: option.avatar,
                                alt: option.name
                            }),
                            /*#__PURE__*/ _jsx(AvatarFallback, {
                                className: "text-xs",
                                style: {
                                    backgroundColor: (_option_avatarColor = option.avatarColor) !== null && _option_avatarColor !== void 0 ? _option_avatarColor : '#6b7280'
                                },
                                children: getInitials(option.name, 2)
                            })
                        ]
                    }, option.id);
                })
            }),
            /*#__PURE__*/ _jsx("span", {
                className: "text-sm text-foreground truncate min-w-0",
                children: generateNameString(value)
            })
        ]
    });
}
export function AvatarCheckAutocomplete(param) {
    let { options, value, onChange, placeholder = 'Select...', className, disabled = false } = param;
    const [open, setOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const triggerRef = useRef(null);
    const [popoverWidth, setPopoverWidth] = useState(undefined);
    const selectedOptions = useMemo(()=>{
        return options.filter((opt)=>{
            var _opt_value;
            return value.includes((_opt_value = opt.value) !== null && _opt_value !== void 0 ? _opt_value : opt.id);
        });
    }, [
        options,
        value
    ]);
    const filteredOptions = useMemo(()=>{
        if (!searchQuery) return options;
        const query = searchQuery.toLowerCase();
        return options.filter((opt)=>opt.name.toLowerCase().includes(query));
    }, [
        options,
        searchQuery
    ]);
    const handleSelect = (optionId)=>{
        const newValue = value.includes(optionId) ? value.filter((id)=>id !== optionId) : [
            ...value,
            optionId
        ];
        onChange(newValue);
    };
    const handleClear = (e)=>{
        e.stopPropagation();
        onChange([]);
    };
    useEffect(()=>{
        if (open && triggerRef.current) {
            const width = triggerRef.current.offsetWidth;
            setPopoverWidth(width);
        }
    }, [
        open
    ]);
    return /*#__PURE__*/ _jsxs(PopoverPrimitive.Root, {
        open: open,
        onOpenChange: setOpen,
        children: [
            /*#__PURE__*/ _jsx(PopoverPrimitive.Trigger, {
                asChild: true,
                disabled: disabled,
                children: /*#__PURE__*/ _jsxs("button", {
                    ref: triggerRef,
                    type: "button",
                    role: "combobox",
                    "aria-expanded": open,
                    className: cn('flex h-9 w-full cursor-pointer items-center justify-between rounded-md border border-input bg-input/30 px-3 py-2 text-sm', 'focus:outline-none focus:ring-1 focus:ring-ring', 'disabled:cursor-not-allowed disabled:opacity-50', 'w-[300px]', className),
                    children: [
                        /*#__PURE__*/ _jsx("span", {
                            className: "flex-1 text-left overflow-hidden",
                            children: selectedOptions.length > 0 ? /*#__PURE__*/ _jsx(SelectedAvatars, {
                                value: selectedOptions
                            }) : /*#__PURE__*/ _jsx("span", {
                                className: "text-foreground",
                                children: placeholder
                            })
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center gap-1 shrink-0",
                            children: [
                                selectedOptions.length > 0 && /*#__PURE__*/ _jsx("button", {
                                    type: "button",
                                    onClick: handleClear,
                                    className: "rounded-sm opacity-50 hover:opacity-100 focus:outline-none focus:ring-1 focus:ring-ring",
                                    "aria-label": "Clear all selections",
                                    children: /*#__PURE__*/ _jsx(XIcon, {
                                        className: "size-4"
                                    })
                                }),
                                /*#__PURE__*/ _jsx(ChevronDownIcon, {
                                    className: "size-4 opacity-50"
                                })
                            ]
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(PopoverPrimitive.Portal, {
                children: /*#__PURE__*/ _jsx(PopoverPrimitive.Content, {
                    className: cn('z-50 overflow-hidden rounded-md border border-input bg-popover text-popover-foreground shadow-md', 'data-[state=open]:animate-in data-[state=closed]:animate-out', 'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0', 'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95', 'data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2'),
                    style: {
                        width: popoverWidth !== undefined ? "".concat(popoverWidth.toString(), "px") : undefined,
                        minWidth: popoverWidth !== undefined ? "".concat(popoverWidth.toString(), "px") : 'var(--radix-popover-trigger-width)'
                    },
                    sideOffset: 4,
                    align: "start",
                    onOpenAutoFocus: (e)=>{
                        e.preventDefault();
                    },
                    children: /*#__PURE__*/ _jsxs(Command, {
                        shouldFilter: false,
                        children: [
                            /*#__PURE__*/ _jsx(CommandInput, {
                                placeholder: "Search...",
                                value: searchQuery,
                                onValueChange: setSearchQuery,
                                className: "text-foreground placeholder:text-muted-foreground",
                                iconClassName: "text-foreground"
                            }),
                            /*#__PURE__*/ _jsxs(CommandList, {
                                children: [
                                    !filteredOptions.length && /*#__PURE__*/ _jsx(CommandEmpty, {
                                        children: "No results found."
                                    }),
                                    /*#__PURE__*/ _jsx(CommandGroup, {
                                        children: filteredOptions.map((option)=>{
                                            var _option_value, _option_avatarColor;
                                            const optionKey = (_option_value = option.value) !== null && _option_value !== void 0 ? _option_value : option.id;
                                            const isSelected = value.includes(optionKey);
                                            return /*#__PURE__*/ _jsxs("div", {
                                                className: cn('relative flex cursor-pointer items-center justify-between rounded-sm px-2 py-1.5 text-sm outline-none select-none', 'hover:bg-accent hover:text-accent-foreground', 'data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground'),
                                                onClick: ()=>{
                                                    handleSelect(optionKey);
                                                },
                                                role: "option",
                                                "aria-selected": isSelected,
                                                children: [
                                                    /*#__PURE__*/ _jsxs("div", {
                                                        className: "flex items-center gap-2 flex-1",
                                                        children: [
                                                            /*#__PURE__*/ _jsxs(Avatar, {
                                                                className: "size-8",
                                                                children: [
                                                                    option.avatar && /*#__PURE__*/ _jsx(AvatarImage, {
                                                                        src: option.avatar,
                                                                        alt: option.name
                                                                    }),
                                                                    /*#__PURE__*/ _jsx(AvatarFallback, {
                                                                        className: "text-sm",
                                                                        style: {
                                                                            backgroundColor: (_option_avatarColor = option.avatarColor) !== null && _option_avatarColor !== void 0 ? _option_avatarColor : '#6b7280'
                                                                        },
                                                                        children: getInitials(option.name, 2)
                                                                    })
                                                                ]
                                                            }),
                                                            /*#__PURE__*/ _jsx("span", {
                                                                children: option.name
                                                            })
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsx("div", {
                                                        className: cn('flex size-4 items-center justify-center rounded border', isSelected ? 'border-accent-turquoise bg-accent-turquoise text-background' : 'border-muted-foreground'),
                                                        children: isSelected && /*#__PURE__*/ _jsx(CheckIcon, {
                                                            className: "size-3"
                                                        })
                                                    })
                                                ]
                                            }, optionKey);
                                        })
                                    })
                                ]
                            })
                        ]
                    })
                })
            })
        ]
    });
}
